<template>
  <div v-if="isLoggedIn">
    <h4>YOU CANNOT ACCESS THIS PAGE WHILE LOGGED IN. PLEASE LOG OUT AND RE-OPEN THE LINK TO ACCESS.</h4>
  </div>
  <div v-else class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Contact Verification</h3>

        <p class="reset__introduction">{{ inviteNotice }}</p>
        <p class="reset__introduction">
          Contact accounts are no longer set up on this page. Your username and
          password are chosen at the pacmny sign-in service, which this application
          never handles.
        </p>
        <p class="reset__introduction">
          Continue to sign in below. If you have not received an invitation from the
          sign-in service, ask the person who invited you to send one.
        </p>
        <p class="reset__introduction invite-expired">
          Invitations expire 30 days after they are sent. If the sign-in service
          tells you "{{ inviteExpired }}", only the person who invited you can send
          a new invitation; requesting a new link will not.
        </p>

        <div class="button-group">
          <button
            type="button"
            class="btn btn--login"
            id="sbmtuserbtn"
            :disabled="redirecting"
            @click="continueToSignIn"
          >
            {{ redirecting ? 'Taking you there...' : 'Continue to sign in' }}
          </button>
          <!-- For an invitation that cannot be found, not an expired one: only
               the inviter sending it again revives an expired invitation. -->
          <a :href="forgotPasswordUrl" class="form__link">Can't find your invitation? Request a new link</a>
          <router-link to="/login" class="form__link">Back to login</router-link>
        </div>

        <div v-if="handoffError" class="form-error-summary" role="alert">{{ handoffError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Where an emailed "you have been named as a contact" link lands.
 *
 * The page used to create the contact's account itself: a full demographic form
 * whose submit posted `/sessionless/register` with a chosen password and then
 * wrote the keyon relationship rows. That is dead — the bridge intercepts
 * `/sessionless/` and maps none of it — and it should not return, because a
 * public endpoint that creates an account with a credential, in an organization
 * named by a query parameter, is the defect the port set out to remove.
 *
 * The port models the invitation properly instead:
 * `POST /contact-invitations` is an authenticated write by someone with a care
 * relationship on the chart, and it mints a **signed, single-use, expiring**
 * token — where `SendContactInvitationEmail` built its link with
 * `$confirmationToken = ""` and a TODO beside it (`keyon/tswebhook.php:7368`), so
 * the invitation was a URL anyone could construct.
 *
 * **What is missing, and is not hidden here:** pacmny-api has no public
 * *acceptance* endpoint for that token — `/contact-invitations` is the send half
 * only — so the `?invitation=` parameter this route still receives has nowhere in
 * the SPA to go. Acceptance belongs with the credential, which is pacmny-auth's,
 * and this page therefore hands off rather than pretending to complete. It never
 * reports success for something it did not do.
 */
import store from '@/store';
import oidc from '@/auth/oidcClient';
import { FORGOT_PASSWORD_URL } from '@/api/identityProvider';
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';
import { INVITE_EXPIRED } from '@/utils/pendingAccountStatus';

export default {
  name: 'verify-contact',
  components: {
    PublicBrandHeader,
  },
  props: {
    invitation: { type: String, default: '' },
    firstName: { type: String, default: '' },
    lastName: { type: String, default: '' },
    email: { type: String, default: '' },
    relationType: { type: String, default: '' },
    inviterAccountNumber: { type: String, default: '' },
    inviterName: { type: String, default: '' },
  },
  data() {
    return {
      // A token-free page on the provider: it answers the same way for a known
      // and an unknown address, so linking to it says nothing about who has an
      // account here. The invitation link itself carries a token and can only be
      // minted and mailed by pacmny-auth. It is offered for an invitation that
      // cannot be found, not for an expired one (utils/pendingAccountStatus.js).
      forgotPasswordUrl: FORGOT_PASSWORD_URL,
      // Main's sentence, word for word (see VerifyAccountPage).
      inviteExpired: INVITE_EXPIRED,
      redirecting: false,
      handoffError: '',
    };
  },
  computed: {
    isLoggedIn() {
      return store.getters['login/isLoggedIn'];
    },
    /**
     * Who invited them, when the link says so. The inviter's name is the one
     * thing that makes an unexpected invitation credible, and it is already in
     * the URL the recipient clicked — nothing new is disclosed by showing it.
     */
    inviteNotice() {
      return this.inviterName
        ? `${this.inviterName} has invited you to be a contact on their care record.`
        : 'You have been invited to be a contact on a patient\'s care record.';
    },
  },
  methods: {
    async continueToSignIn() {
      this.redirecting = true;
      this.handoffError = '';
      try {
        await oidc.beginLogin({ returnTo: '/welcome' });
      } catch (err) {
        // A full-page redirect that never happens leaves the button spinning
        // forever, so an unreachable provider has to be said out loud.
        this.redirecting = false;
        this.handoffError = 'The sign-in service could not be reached. Please try again in a moment.';
        console.error('Could not start sign-in:', err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";
$color-base: #0060ff;
.btn--login {
  color: #fff;
  background-color: $color-base;

  &:hover {
    background-color: lighten($color-base, 5%);
  }
}
.form__link {
  color: white;
  text-decoration: none;
}
#sbmtuserbtn {
  border-radius: 0.375em;
}
.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed; // TODO image subject to copyright
  background-size: cover;
  position: relative;
}
.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}

.reset {
  width: 60% !important;
  background-color: #fff;
  padding: 2rem;
  -moz-box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);

  @include absoluteCenter(380px);

  > h3 {
    text-align: center;
  }
}
.reset__introduction {
  color: darken(gray, 10%);
  font-size: 14px;
  text-align: center;
}

.button-group {
  margin-top: 1.5rem;

  .form__link {
    color: $color-base;
    font-size: 0.85rem;
    margin-top: 0.5rem;
    display: block;

    &:hover {
      text-decoration: underline;
    }
  }
}

.form-error-summary {
  margin: 0.75rem 0 0;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
  color: #9b2c2c;
}

@media only screen and (max-width: 768px) {
  .reset {
    width: 90% !important;
    height: 90%;
    overflow: auto;
  }
}
</style>
